import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, Save, Ruler, Shirt, Scissors, FileText, 
  Trash2, Download, Upload, Plus, Check, Loader2, Copy,
  ChevronDown, ChevronUp, User, Search, UserPlus, X,
  Mail, Phone, ChevronRight, Wand2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import SuitMockupPreview from "@/components/SuitMockupPreview";
import ButtonStyleSelect from "@/components/ButtonStyleSelect";
import CustomStyleCodes from "@/components/CustomStyleCodes";
import GarmentFabricSection from "@/components/GarmentFabricSection";
import LiningMatchRow from "@/components/LiningMatchRow";
import SectionLiningRow from "@/components/SectionLiningRow";
import InnerFacingPipingRow from "@/components/InnerFacingPipingRow";
import LoadStylePresetButton from "@/components/LoadStylePresetButton";
import AutoStyleFields from "@/components/AutoStyleFields";
import ShoulderPadPair from "@/components/ShoulderPadPair";
import ShoulderPosturePair from "@/components/ShoulderPosturePair";
import PantTuxedoToggle, { isPantTuxedoActive } from "@/components/PantTuxedoToggle";
import OrderActions from "@/components/OrderActions";
import MakeToggle from "@/components/MakeToggle";
import MeasurementWarning, { validateMeasurement } from "@/components/MeasurementWarning";
import { MAKE_PROFILES } from "@/lib/makePricing";
import PantFrontButtonRow from "@/components/PantFrontButtonRow";
import StyleSubSection from "@/components/StyleSubSection";
import useApplyStyleDefaults from "@/hooks/useApplyStyleDefaults";
import { JACKET_MAX_DEFAULTS, PANT_MAX_DEFAULTS } from "@/data/styleDefaults";
import { GLYPH_SETS } from "@/components/StyleIconCard";
import { getStyleCode } from "@/data/styleCodes";
import { consumeStyleCart } from "@/data/styleCart";
import { fieldGarmentBucket, fieldStateKey } from "@/data/codeReverseLookup";
import { additionalFieldOptions, overridesLoaded, subscribeOverrides } from "@/data/rcCodeOverrides";
import { useMobileMode } from "@/hooks/useMobileMode";
import MobileModeToggle from "@/components/mobile/MobileModeToggle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Garment Types
const GARMENT_TYPES = [
  "2-Piece Suit", "3-Piece Suit", "Sport Coat", "Trousers", 
  "Vest", "Dress Shirt", "Overcoat", "Tuxedo"
];

// Adjustment options
const ADJUSTMENT_OPTIONS = [
  { value: -6, label: "-6" }, { value: -5.75, label: "-5¾" }, { value: -5.5, label: "-5½" }, { value: -5.25, label: "-5¼" },
  { value: -5, label: "-5" }, { value: -4.75, label: "-4¾" }, { value: -4.5, label: "-4½" }, { value: -4.25, label: "-4¼" },
  { value: -4, label: "-4" }, { value: -3.75, label: "-3¾" }, { value: -3.5, label: "-3½" }, { value: -3.25, label: "-3¼" },
  { value: -3, label: "-3" }, { value: -2.75, label: "-2¾" }, { value: -2.5, label: "-2½" }, { value: -2.25, label: "-2¼" },
  { value: -2, label: "-2" }, { value: -1.75, label: "-1¾" }, { value: -1.5, label: "-1½" }, { value: -1.25, label: "-1¼" },
  { value: -1, label: "-1" }, { value: -0.75, label: "-¾" }, { value: -0.5, label: "-½" }, { value: -0.25, label: "-¼" },
  { value: 0, label: "0" },
  { value: 0.25, label: "+¼" }, { value: 0.5, label: "+½" }, { value: 0.75, label: "+¾" }, { value: 1, label: "+1" },
  { value: 1.25, label: "+1¼" }, { value: 1.5, label: "+1½" }, { value: 1.75, label: "+1¾" }, { value: 2, label: "+2" },
  { value: 2.25, label: "+2¼" }, { value: 2.5, label: "+2½" }, { value: 2.75, label: "+2¾" }, { value: 3, label: "+3" },
  { value: 3.25, label: "+3¼" }, { value: 3.5, label: "+3½" }, { value: 3.75, label: "+3¾" }, { value: 4, label: "+4" },
  { value: 4.25, label: "+4¼" }, { value: 4.5, label: "+4½" }, { value: 4.75, label: "+4¾" }, { value: 5, label: "+5" },
  { value: 5.25, label: "+5¼" }, { value: 5.5, label: "+5½" }, { value: 5.75, label: "+5¾" }, { value: 6, label: "+6" },
];

// ============= BODY POSTURE OPTIONS =============
const BACK_BODY_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "backward_0.5", label: "Body Backward (0.5cm)" },
  { value: "backward_0.8", label: "Body Backward (0.8cm)" },
  { value: "backward_1.0", label: "Body Backward (1.0cm)" },
  { value: "backward_1.5", label: "Body Backward (1.5cm)" },
  { value: "backward_2.0", label: "Body Backward (2.0cm)" },
  { value: "forward_0.5", label: "Body Forward (0.5cm)" },
  { value: "forward_0.8", label: "Body Forward (0.8cm)" },
  { value: "forward_1.0", label: "Body Forward (1.0cm)" },
  { value: "forward_1.5", label: "Body Forward (1.5cm)" },
  { value: "forward_2.0", label: "Body Forward (2.0cm)" },
];

const BACK_POSTURE_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "slight_humpback", label: "Slight Humpback" },
  { value: "moderate_humpback", label: "Moderate Humpback" },
  { value: "very_humpback", label: "Very Humpback" },
  { value: "stoop_1.0", label: "Stoop (1.0cm)" },
  { value: "stoop_2.0", label: "Stoop (2.0cm)" },
  { value: "stoop_3.0", label: "Stoop (3.0cm)" },
  { value: "slight_concave", label: "Slightly Concave Waist" },
  { value: "very_concave", label: "Very Concave Waist" },
];

const SHOULDER_BLADE_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "bulge", label: "Bulge Shoulder Blade" },
  { value: "bulge_top", label: "Bulge Top Shoulder Blade" },
  { value: "inner_higher", label: "Inner Shoulder Blade Higher" },
];

const NECK_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "slight_forward", label: "Slight Forward Head" },
  { value: "moderate_forward", label: "Moderate Forward Head" },
  { value: "serious_forward", label: "Serious Forward Head" },
];

const LOWER_COLLAR_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "lower_0.5", label: "Lower Collar 0.5cm" },
  { value: "lower_1.0", label: "Lower Collar 1.0cm" },
  { value: "lower_1.5", label: "Lower Collar 1.5cm" },
  { value: "lower_2.0", label: "Lower Collar 2.0cm" },
  { value: "raise_0.5", label: "Raise Collar 0.5cm" },
  { value: "raise_1.0", label: "Raise Collar 1.0cm" },
  { value: "raise_1.5", label: "Raise Collar 1.5cm" },
  { value: "raise_2.0", label: "Raise Collar 2.0cm" },
];

const CHEST_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "slight_out", label: "Slight Chest Out" },
  { value: "serious_out", label: "Serious Chest Out" },
  { value: "well_developed", label: "Well-developed Chest" },
];

const ARM_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "slight_backward", label: "Slight Arms Backward" },
  { value: "very_backward", label: "Very Arms Backward" },
  { value: "slight_forward", label: "Slight Arms Forward" },
  { value: "very_forward", label: "Very Arms Forward" },
  { value: "curved", label: "Curved Arm" },
  { value: "straight", label: "Straight Arm" },
];

const WAIST_HEIGHT_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "higher_1.0", label: "Higher Waist 1.0cm" },
  { value: "higher_2.0", label: "Higher Waist 2.0cm" },
  { value: "higher_3.0", label: "Higher Waist 3.0cm" },
  { value: "higher_4.0", label: "Higher Waist 4.0cm" },
  { value: "lower_1.0", label: "Lower Waist 1.0cm" },
  { value: "lower_2.0", label: "Lower Waist 2.0cm" },
  { value: "lower_3.0", label: "Lower Waist 3.0cm" },
  { value: "lower_4.0", label: "Lower Waist 4.0cm" },
];

const SHOULDER_OPTIONS = [
  { value: "normal", label: "Normal-A" },
  { value: "slight_slope_b", label: "Slight Slope-B" },
  { value: "moderate_slope_c", label: "Moderate Slope-C" },
  { value: "very_slope_d", label: "Very Slope-D" },
  { value: "slight_high_e", label: "Slight High-E" },
  { value: "half_high_f", label: "Half High-F" },
  { value: "high_g", label: "High-G" },
  { value: "square", label: "Square" },
  { value: "rounded", label: "Rounded" },
];

const ABDOMEN_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "flat", label: "Flat Stomach" },
  { value: "prominent", label: "Prominent Stomach" },
  { value: "very_prominent", label: "Very Prominent Stomach" },
];

const SEAT_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "flat", label: "Flat Seat" },
  { value: "prominent", label: "Prominent Seat" },
  { value: "very_prominent", label: "Very Prominent Seat" },
];

// Rise posture options (matching POS)
const RISE_POSTURE_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "high_1", label: "High +1cm" },
  { value: "high_2", label: "High +2cm" },
  { value: "low_1", label: "Low -1cm" },
  { value: "low_2", label: "Low -2cm" },
];

// ============= JACKET STYLING OPTIONS =============
const JACKET_BODY_STYLES = ["Slim", "Regular", "Classic", "Relaxed"];  // kept for legacy state, no longer rendered
const JACKET_CANVAS_CONSTRUCTIONS = [
  "Full Canvas", "Half Canvas", "Fused", "Medium Fused",
  "Full Handmade & Full Canvas", "Half Handmade & Full Canvas",
  "Full Handmade & Half Canvas", "Half Handmade & Half Canvas",
  "Unconstructed Canvas", "Full Handmade Unconstructed", "Half Handmade Unconstructed",
  "Single Chest + Single Shoulder Pad", "Single Chest + Regular Shoulder Pad",
];
const JACKET_FRONT_CLOSURES = ["1-Button", "2-Button", "3-Button", "3-Roll-2", "4-Button", "2x1 DB", "4x2 DB", "6x1 DB", "6x2 DB", "8x2 DB"];
const LAPEL_TYPES = ["Notch", "Peak", "Peak Lapel", "Extended Peak", "Shawl"];
const LAPEL_WIDTHS = ["2.5in (6.4cm)", "3in (7.6cm)", "3.5in (8.9cm)", "3.75in (9.5cm)", "4in (10.2cm)", "4.5in (11.4cm)", "5in (12.7cm)", "5.5in (14cm)", "6in (15.2cm)"];
const GORGE_LINES = ["Standard", "+1cm (0.4in)", "-2cm (0.8in)", "-5cm (2in)"];
const WAIST_POCKET_TYPES = [
  "Welt", "Flap", "Patch", "Double Besom", "No Pocket",
  "Normal Pocket + Ticket Pocket",
  "Normal Slanted Pocket + Ticket Pocket",
];
const WAIST_POCKET_ANGLES = ["Straight", "Slanted"];
const TICKET_POCKETS = ["None", "Welt", "Flap", "Patch", "Double Besom"];
const CHEST_POCKETS = ["None", "Welt", "Patch", "Double Besom"];
const BUTTON_STYLES = ["Horn", "Corozo", "Mother of Pearl", "Metal"];
const EXTERIOR_STITCHES = ["None", "1/16\" Pic", "5/16\" Pic", "1/16\" Top", "5/16\" Top", "1/4\" Double Top", "1/4\" Double Pic"];
const SLEEVE_BUTTONHOLES = ["4 Kissing", "4 Non-Kissing", "3 Kissing", "3 Non-Kissing", "No Buttons"];
const VENTS = ["Single Vent", "Double Vent", "No Vent"];
const LAPEL_BUTTONHOLES = ["None", "Working Buttonhole", "Milanese Hole", "Keyhole"];
const CHEST_POCKET_POSTURES = ["Standard", "+0.5cm (0.2in) Higher", "+1cm (0.4in) Higher", "+1.5cm (0.6in) Higher", "-0.5cm (0.2in) Lower", "-1cm (0.4in) Lower", "-1.5cm (0.6in) Lower"];
const LOWER_POCKET_FLAP_WIDTHS = ["Standard 5cm (2in)", "4cm (1.6in)", "4.5cm (1.8in)", "5.5cm (2.2in)", "6cm (2.4in)", "6.5cm (2.6in)", "7cm (2.8in)", "7.5cm (3in)", "8cm (3.1in)", "8.5cm (3.3in)", "9cm (3.5in)"];
const TICKET_POCKET_POSTURES = ["Standard", "+0.5cm (0.2in) Higher", "+1cm (0.4in) Higher", "+1.5cm (0.6in) Higher", "+2cm (0.8in) Higher", "-0.5cm (0.2in) Lower", "-1cm (0.4in) Lower", "-1.5cm (0.6in) Lower", "-2cm (0.8in) Lower"];
const BACK_VENT_LENGTHS = ["Standard", "+1cm (0.4in)", "+2cm (0.8in)", "+3cm (1.2in)", "+4cm (1.6in)", "+5cm (2in)", "+6cm (2.4in)", "-1cm (0.4in)", "-2cm (0.8in)", "-3cm (1.2in)", "-4cm (1.6in)", "-5cm (2in)", "-6cm (2.4in)"];
const ARMHOLE_ADJUSTMENTS = ["Standard", "+1cm (0.4in) Higher", "+2cm (0.8in) Higher", "-1cm (0.4in) Lower", "-2cm (0.8in) Lower"];
const SHOULDER_POSTURE_ADJUSTMENTS = ["Standard", "Square +0.5cm (0.2in)", "Square +1cm (0.4in)", "Square +1.5cm (0.6in)", "Square +2cm (0.8in)", "Sloped -0.5cm (0.2in)", "Sloped -1cm (0.4in)", "Sloped -1.5cm (0.6in)", "Sloped -2cm (0.8in)", "Forward Posture"];

// ============= PANT STYLING OPTIONS =============
const PANT_BODY_STYLES = ["Slim", "Regular", "Classic", "Relaxed"];
const PANT_FRONT_STYLES = [
  "Flat Front",
  "Plain Front w/ Single Dart",
  "Plain Front w/ Double Darts",
  "Single Pleat",
  "Single Pleat fwd Fly",
  "Double Pleat",
  "Double Pleats fwd Sideseam",
  "Triple Pleat",
  "One Opposite Pleat",
  "One Box Pleat",
];
const PANT_PLEAT_DEPTHS = ["—", "1.5cm", "2.0cm", "2.5cm", "3.2cm", "3.8cm", "4.4cm"];
const FRONT_POCKETS = [
  "Slant", "2.0cm Slant", "2.5cm Slant", "3.2cm Slant", "5.1cm Slant", "Very Slant",
  "On-Seam", "Sideseam", "Double Besom", "Welt", "Front Welt w/ Zipper",
  "Square Jeans", "Round Jeans", "Arc Jeans", "Diamond Jeans", "DL Jeans", "No Pocket",
];
const FRONT_POCKET_BARTACKS = ["None", "L Shape", "D Bartack", "Dash (-) Bartack", "X Bartack"];
const FRONT_POCKET_STITCHES = [
  "None", "0.15 Top Stitch", "0.15 Pick Stitch", "0.3 Top Stitch",
  "0.6 Top Stitch", "0.6 Pick Stitch", "0.6cm Wide Pick Stitch",
];
const WATCH_POCKETS = [
  "None", "Normal Left", "Normal Right", "Normal Both",
  "Besom Left", "Welt Right", "Double Besom Left", "Double Besom Right", "Double Besom Both",
  "Right + Diamond Flap", "Right + Triangle Flap", "Right + Square Flap",
  "Left + Diamond Flap", "Left + Square Flap",
];
const INNER_WATCH_POCKETS = ["None", "Left", "Right", "Both", "ETQ w/ Zip Inside"];
const BACK_POCKET_STYLES = [
  "No Pocket",
  "Double Besom",
  "Double Besom + Button",
  "Double Besom + Tab + Button",
  "Double Besom + Pointed Tab + Button",
  "Double Besom Hidden Button",
  "Welt",
  "Welt + Loop + Button",
  "Welt + Zipper",
  "Besom Back",
  "Back w/ Flap",
  "Back w/ Diamond Flap",
  "Back w/ Diamond Flap + Button",
  "Back w/ Pointed Flap",
  "Back w/ Pointed Flap + Button",
  "Pointed Patch",
  "Round Patch",
  "Round Patch + Button",
  "Knife Shape + Button",
  "Back Zipper",
];
const BACK_POCKET_CLOSURES = ["Button", "No Button", "Tab & Button", "Pointed Tab & Button", "Square Tab & Button", "Hidden Button", "Zipper", "Loop & Button"];
const BOTTOM_FINISHINGS = ["Plain Hem", "Cuff 1.5\"", "Cuff 1.75\"", "Cuff 2\""];
const FRONT_CLOSURES_PANT = ["Button & Tab", "Hook & Bar", "Extended Tab"];
const WAISTBAND_STYLES = [
  "Standard",
  "Front & Back Continuous",
  "Uncontinued Front + Continued Back",
  "Front Continued",
  "Side Tabs",
  "Belt Loops",
];
const WAISTBAND_WIDTHS = ["1in (2.5cm)", "1.5in (3.8cm)", "2in (5.1cm)", "2.5in (6.4cm)", "3in (7.6cm)", "3.5in (8.9cm)", "4in (10.2cm)", "4.5in (11.4cm)", "5in (12.7cm)"];

// ============= SHIRT STYLING OPTIONS =============
const SHIRT_COLLAR_STYLES = [
  "Spread", "Cutaway", "Wide Spread", "Semi Spread", "Standard",
  "Button-Down", "Tab Collar", "Pin Collar", "Club", "Band", "Wing", "Cuban", "Hidden Placket",
];
const SHIRT_COLLAR_HEIGHTS = ["Standard 4cm", "3.5cm", "4.5cm", "5cm"];
const SHIRT_COLLAR_STAYS = ["Removable", "Sewn-In", "No Stays"];
const SHIRT_CUFF_STYLES = [
  "Single Button Barrel", "Two Button Barrel", "Convertible", "French",
  "Mitered Barrel", "Rounded Barrel", "Square Barrel", "Angled Barrel",
];
const SHIRT_PLACKETS = ["Standard", "French Placket", "Hidden Placket", "Pleat Front", "Tuxedo Bib"];
const SHIRT_POCKETS = ["None", "Standard", "Standard w/ Flap", "Rounded", "Pointed", "Two Pockets", "Western", "With Pen Slot"];
const SHIRT_BACKS = ["Plain", "Side Pleats", "Box Pleat", "Darts", "Yoke Split"];
const SHIRT_BOTTOMS = ["Curved", "Straight", "Side Slit"];
const SHIRT_MONOGRAMS = ["None", "Left Cuff", "Right Cuff", "Chest", "Pocket", "Bottom"];

// ============= VEST STYLING OPTIONS =============
const VEST_LAPELS = ["None", "Notch", "Peak", "Shawl"];
const VEST_FRONT_CLOSURES = ["5-Button", "6-Button", "4-Button", "DB 6x3"];
const VEST_POCKET_TYPES = ["Welt", "Patch", "Double Besom Pocket", "No Pockets"];
const VEST_POCKET_NUMBERS = ["2 Pockets", "4 Pockets"];
const VEST_BACKS = ["Same Fabric", "Lining", "Belt Back"];

// Style Select Component (renders RC factory code beside option name)
const StyleSelect = ({ label, value, onChange, options, codePrefix, iconKind }) => {
  // Re-render when admin overrides hydrate so newly-added codes show up live.
  const [, tick] = useState(0);
  useEffect(() => subscribeOverrides(() => tick((n) => n + 1)), []);
  const extras = codePrefix && overridesLoaded() ? additionalFieldOptions(codePrefix, options) : [];
  const mergedOptions = extras.length ? [...options, ...extras] : options;
  const selected = value || mergedOptions[0];
  const glyphs = iconKind ? GLYPH_SETS[iconKind] || {} : null;
  const Glyph = ({ opt, size = 16 }) => {
    if (!glyphs) return null;
    const tokenize = (s) => String(s || "").toLowerCase().split(/[\s/\-_,]+/).filter(Boolean);
    const direct = glyphs[opt];
    let g = direct;
    if (!g) {
      const norm = String(opt || "").toLowerCase();
      const optTokens = new Set(tokenize(opt));
      const key = Object.keys(glyphs).find((k) => {
        const lk = k.toLowerCase();
        if (norm.includes(lk) || lk.includes(norm)) return true;
        const keyTokens = tokenize(k);
        return keyTokens.length > 0 && keyTokens.every((t) => optTokens.has(t));
      });
      if (key) g = glyphs[key];
    }
    if (!g) return null;
    return (
      <span className="inline-flex items-center justify-center text-amber-300 shrink-0" style={{ width: size, height: size }}>
        {g}
      </span>
    );
  };
  return (
    <div>
      <label className="text-xs text-slate-400 block mb-1">{label}</label>
      <Select value={selected} onValueChange={onChange}>
        <SelectTrigger className="bg-background border-white/10 h-8 text-xs">
          <SelectValue>
            <span className="flex items-center gap-1.5 truncate">
              <Glyph opt={selected} size={14} />
              <span className="truncate">{selected}</span>
              {(() => {
                const c = getStyleCode(codePrefix, mergedOptions, selected);
                return c ? <code className="text-[10px] text-amber-400/80 font-mono shrink-0">{c}</code> : null;
              })()}
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {mergedOptions.map((opt) => {
            const c = getStyleCode(codePrefix, mergedOptions, opt);
            const isExtra = extras.includes(opt);
            return (
              <SelectItem key={opt} value={opt} className="text-xs">
                <span className="flex items-center gap-2 w-full">
                  <Glyph opt={opt} size={16} />
                  <span className="flex-1">{opt}{isExtra && <span className="text-amber-400 ml-1 text-[9px]">★</span>}</span>
                  {c && <code className="text-[10px] text-amber-400/80 font-mono">{c}</code>}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};

// Posture Select Component
const PostureSelect = ({ label, value, onChange, options }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-white/5">
    <span className="text-xs text-slate-400">{label}</span>
    <Select value={value || "normal"} onValueChange={onChange}>
      <SelectTrigger className="w-44 h-7 text-xs bg-background border-white/10">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map(opt => (
          <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

// Measurement Row Component - with editable starting value and final override
const MeasurementRow = ({ label, fieldKey, allMeasurements = {}, startingValue, adjustment, onStartingChange, onAdjustmentChange }) => {
  const start = parseFloat(startingValue) || 0;
  const adj = parseFloat(adjustment) || 0;
  const finalValue = (start + adj).toFixed(2);
  // Only validate when the user has entered a starting value — empty rows
  // shouldn't show warnings just because 0 is outside the expected range.
  const hasStarting = startingValue !== undefined && startingValue !== null && String(startingValue).trim() !== "";

  // Cross-field validation: pass a flat snapshot of every measurement's
  // starting value so cross-field checks (thigh > seat etc.) work.
  const flatSnapshot = useMemo(() => {
    const out = {};
    for (const [k, v] of Object.entries(allMeasurements || {})) {
      if (v && typeof v === "object") {
        out[k] = parseFloat(v.starting) || 0;
      } else {
        out[k] = v;
      }
    }
    return out;
  }, [allMeasurements]);
  const warning = hasStarting ? validateMeasurement(fieldKey, finalValue, flatSnapshot) : null;

  return (
    <tr className="border-b border-white/5">
      <td className="py-1.5 px-2 text-xs">{label}</td>
      <td className="py-1.5 px-1 text-center">
        <Input 
          type="number" 
          step="0.01"
          value={startingValue || ""} 
          onChange={(e) => onStartingChange(e.target.value)}
          placeholder="-"
          className="w-16 h-7 text-xs bg-background border-white/10 text-center mx-auto"
        />
      </td>
      <td className="py-1.5 px-1 text-center">
        <select
          value={adjustment || 0}
          onChange={(e) => onAdjustmentChange(parseFloat(e.target.value) || 0)}
          className="w-20 h-7 text-xs bg-[#0a1628] border border-white/20 rounded text-center text-white appearance-none cursor-pointer"
          data-testid={`adj-select-${label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {ADJUSTMENT_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </td>
      <td className="py-1.5 px-1 text-center">
        <div className="flex items-center justify-center gap-1.5">
          <span className="text-xs text-primary font-medium">{finalValue}</span>
          <MeasurementWarning warning={warning} />
        </div>
      </td>
    </tr>
  );
};

const AdminQuickOrderPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Template state
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  
  // Customer state
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [clients, setClients] = useState([]);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClientForm, setNewClientForm] = useState({
    name: "", email: "", phone: "", address: ""
  });
  const [creatingClient, setCreatingClient] = useState(false);
  
  // Size charts state
  const [sizeCharts, setSizeCharts] = useState({ jacket: [], pant: [], vest: [], shirt: [] });
  const [jacketSize, setJacketSize] = useState("");
  const [pantSize, setPantSize] = useState("");
  const [vestSize, setVestSize] = useState("");
  const [shirtSize, setShirtSize] = useState("");
  
  // Form state
  const [garmentType, setGarmentType] = useState("2-Piece Suit");
  // RC make / CTD make — gates which construction list shows and which
  // pricing pipeline runs. Default RC for parity with historical orders.
  const [make, setMake] = useState("rc");
  // Per-garment fabric rc_codes — { jacket: "DBV185A", pant: "...", vest, shirt, overcoat }
  const [fabricByGarment, setFabricByGarment] = useState({});
  const [liningByGarment, setLiningByGarment] = useState({});
  // Tracks the last value the lining cascade pushed into each follower
  // field so we can detect user-initiated overrides and stop overwriting
  // them on the next Body Lining change.
  const liningCascadeRef = useRef({ sleeve: null, facing: null, pant: null, vest: null });
  // Same idea for Match Buttons — last source code we pushed across the
  // button slots. Future Match clicks skip overridden ones.
  const buttonMatchSourceRef = useRef(null);
  // Tracks the id of the most-recently-saved MTM order so the OrderActions
  // (PDF / SMS / Email) can target it without re-saving.
  const [lastSavedOrderId, setLastSavedOrderId] = useState("");
  const [lastSavedOrderNumber, setLastSavedOrderNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [shirtRemarks, setShirtRemarks] = useState("");
  
  // Sections expanded state
  const [expandedSections, setExpandedSections] = useState({
    jacketMeasurements: true,
    jacketPosture: false,
    jacketStyling: true,
    pantMeasurements: true,
    pantPosture: false,
    pantStyling: true,
    vestMeasurements: false,
    vestStyling: false,
    shirtMeasurements: true,
    shirtStyling: true
  });
  
  // Jacket measurements (matching POS exactly)
  const [jacketMeasurements, setJacketMeasurements] = useState({
    chest: { starting: "", adjustment: 0 },
    stomach: { starting: "", adjustment: 0 },
    seat: { starting: "", adjustment: 0 },
    shoulder: { starting: "", adjustment: 0 },
    bicep: { starting: "", adjustment: 0 },
    right_sleeve_length: { starting: "", adjustment: 0 },
    left_sleeve_length: { starting: "", adjustment: 0 },
    back_length: { starting: "", adjustment: 0 },
    front_length: { starting: "", adjustment: 0 },
    right_cuff: { starting: "", adjustment: 0 },
    left_cuff: { starting: "", adjustment: 0 },
    button_stance: { starting: "", adjustment: 0 }
  });
  
  // Jacket posture
  const [jacketPosture, setJacketPosture] = useState({
    back_body: "normal",
    back_posture: "normal",
    shoulder_blade: "normal",
    neck: "normal",
    chest: "normal",
    arm: "normal",
    waist_height: "normal",
    left_shoulder: "normal",
    right_shoulder: "normal"
  });
  
  // Pant measurements (matching POS exactly)
  const [pantMeasurements, setPantMeasurements] = useState({
    waist: { starting: "", adjustment: 0 },
    seat: { starting: "", adjustment: 0 },
    rise: { starting: "", adjustment: 0 },
    thigh: { starting: "", adjustment: 0 },
    right_pant_length: { starting: "", adjustment: 0 },
    left_pant_length: { starting: "", adjustment: 0 },
    knee: { starting: "", adjustment: 0 },
    bottom: { starting: "", adjustment: 0 }
  });
  
  // Pant posture (matching POS exactly)
  const [pantPosture, setPantPosture] = useState({
    front_rise: "normal",
    back_rise: "normal"
  });
  
  // Vest measurements (matching POS exactly)
  const [vestMeasurements, setVestMeasurements] = useState({
    chest: { starting: "", adjustment: 0 },
    stomach: { starting: "", adjustment: 0 },
    back_length: { starting: "", adjustment: 0 },
    front_length: { starting: "", adjustment: 0 },
    bottom: { starting: "", adjustment: 0 }
  });
  
  // Shirt measurements
  const [shirtMeasurements, setShirtMeasurements] = useState({
    neck: { starting: "", adjustment: 0 },
    chest: { starting: "", adjustment: 0 },
    stomach: { starting: "", adjustment: 0 },
    seat: { starting: "", adjustment: 0 },
    shoulder: { starting: "", adjustment: 0 },
    right_sleeve_length: { starting: "", adjustment: 0 },
    left_sleeve_length: { starting: "", adjustment: 0 },
    back_length: { starting: "", adjustment: 0 },
    right_cuff: { starting: "", adjustment: 0 },
    left_cuff: { starting: "", adjustment: 0 }
  });
  
  // Jacket styling (ALL options from POS)
  const [jacketStyling, setJacketStyling] = useState({
    canvas_construction: "Half Canvas",
    front_closure: "2-Button",
    lapel_type: "Notch",
    lapel_width: "3.5in (8.9cm)",
    gorge_line: "Standard",
    waist_pocket_type: "Flap",
    waist_pocket_angle: "Straight",
    ticket_pocket: "None",
    chest_pocket: "Welt",
    button_style: "Horn",
    exterior_stitch: "None",
    sleeve_buttonholes: "4 Kissing",
    vent: "Double Vent",
    lapel_buttonhole: "None",
    chest_pocket_posture: "Standard",
    lower_pocket_flap_width: "Standard 5cm (2in)",
    ticket_pocket_posture: "Standard",
    back_vent_length: "Standard",
    armhole_adjustment: "Standard",
    shoulder_posture: "Standard",
    under_collar_monogram: "",
    lining_color: "",
    sleeve_lining_color: ""
  });
  
  // Pant styling (ALL options from POS)
  const [pantStyling, setPantStyling] = useState({
    body_style: "Regular",
    front_style: "Flat Front",
    front_pocket: "Slant",
    back_pocket_style: "Double Besom",
    back_pocket_closure: "Button",
    bottom_finishing: "Plain Hem",
    front_closure: "Button & Tab",
    waistband_style: "Standard",
    waistband_width: "1.5in (3.8cm)",
    pleat_depth: "—",
    watch_pocket: "None",
    inner_watch_pocket: "None",
    front_pocket_bartack: "None",
    front_pocket_stitch: "None",
    pant_lining_color: ""
  });
  
  // Vest styling (ALL options from POS)
  const [vestStyling, setVestStyling] = useState({
    lapel: "None",
    front_closure: "5-Button",
    pocket_type: "Welt",
    pocket_number: "4 Pockets",
    back: "Lining"
  });

  const [shirtStyling, setShirtStyling] = useState({
    collar_style:  "Spread",
    collar_height: "Standard 4cm",
    collar_stays:  "Removable",
    cuff_style:    "Single Button Barrel",
    placket:       "Standard",
    pocket:        "Standard",
    back:          "Plain",
    bottom:        "Curved",
    button_style:  "Standard",
    monogram:      "None",
  });

  // Remarks
  const [jacketRemarks, setJacketRemarks] = useState("");
  const [pantRemarks, setPantRemarks] = useState("");
  const [vestRemarks, setVestRemarks] = useState("");

  // Auto-default factory finishing fields on first mount (only fills blanks).
  useApplyStyleDefaults(jacketStyling, setJacketStyling, JACKET_MAX_DEFAULTS);
  useApplyStyleDefaults(pantStyling, setPantStyling, PANT_MAX_DEFAULTS);
  
  // Mockup Preview
  const [showMockupPreview, setShowMockupPreview] = useState(false);

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") {
      navigate("/admin");
    } else {
      setIsAuthenticated(true);
      fetchTemplates();
      fetchSizeCharts();
      fetchClients();
    }
  }, [navigate]);

  // Consume any styles staged by the RC Code Book and apply them to the matching dropdowns.
  useEffect(() => {
    const { items } = consumeStyleCart();
    if (!items.length) return;
    const byBucket = { jacket: {}, pant: {}, vest: {}, shirt: {} };
    for (const it of items) {
      const bucket = fieldGarmentBucket(it.field);
      const key = fieldStateKey(it.field);
      if (bucket && key) byBucket[bucket][key] = it.value;
    }
    if (Object.keys(byBucket.jacket).length) setJacketStyling((s) => ({ ...s, ...byBucket.jacket }));
    if (Object.keys(byBucket.pant).length)   setPantStyling((s)   => ({ ...s, ...byBucket.pant   }));
    if (Object.keys(byBucket.vest).length)   setVestStyling((s)   => ({ ...s, ...byBucket.vest   }));
    if (Object.keys(byBucket.shirt).length)  setShirtStyling((s)  => ({ ...s, ...byBucket.shirt  }));
    toast.success(`Applied ${items.length} style${items.length === 1 ? "" : "s"} from RC Code Book`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Side Tabs auto-binding: T-3329 (No belt loop) + T-3352 (Adjust buckle QZMAB01-2)
  // ride along automatically when Side Tabs is picked in the Waistband Style dropdown.
  useEffect(() => {
    const SIDE_TAB_CODES = [
      { code: "T-3329", label: "No belt loop",            field: "pant_belt_loop_style" },
      { code: "T-3352", label: "Adjust buckle QZMAB01-2", field: "pant_waist_adjustment" },
    ];
    const isSideTabs = pantStyling.waistband_style === "Side Tabs";
    const current = pantStyling.custom_codes || [];
    const hasAll = SIDE_TAB_CODES.every((c) => current.some((x) => x.code === c.code));
    if (isSideTabs && !hasAll) {
      const toAdd = SIDE_TAB_CODES
        .filter((c) => !current.some((x) => x.code === c.code))
        .map((c) => ({ id: `${c.code}-sidetabs`, code: c.code, label: c.label, field: c.field, auto: true }));
      setPantStyling((s) => ({ ...s, custom_codes: [...(s.custom_codes || []), ...toAdd] }));
    } else if (!isSideTabs && current.some((x) => x.auto && SIDE_TAB_CODES.some((c) => c.code === x.code))) {
      setPantStyling((s) => ({
        ...s,
        custom_codes: (s.custom_codes || []).filter((x) => !(x.auto && SIDE_TAB_CODES.some((c) => c.code === x.code))),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pantStyling.waistband_style]);

  const fetchSizeCharts = async () => {
    try {
      const response = await axios.get(`${API}/size-chart/all`);
      setSizeCharts(response.data || { jacket: [], pant: [], vest: [], shirt: [] });
    } catch (error) {
      console.error("Error fetching size charts:", error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/crm/clients`);
      setClients(response.data || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/quick-order/templates`);
      setTemplates(response.data || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error("Please enter a template name");
      return;
    }
    
    setIsSaving(true);
    
    try {
      const templateData = {
        name: templateName,
        garment_type: garmentType,
        jacket_size: jacketSize,
        pant_size: pantSize,
        vest_size: vestSize,
        jacket_measurements: jacketMeasurements,
        pant_measurements: pantMeasurements,
        vest_measurements: vestMeasurements,
        jacket_styling: jacketStyling,
        pant_styling: pantStyling,
        vest_styling: vestStyling,
        shirt_styling: shirtStyling,
        body_posture: {
          jacket: jacketPosture,
          pant: pantPosture
        },
        remarks: {
          jacket: jacketRemarks,
          pant: pantRemarks,
          vest: vestRemarks
        },
        notes: notes
      };
      
      await axios.post(`${API}/admin/quick-order/templates`, templateData);
      
      toast.success("Template saved successfully!");
      setShowSaveDialog(false);
      setTemplateName("");
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to save template");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoadTemplate = (template) => {
    setGarmentType(template.garment_type || "2-Piece Suit");
    if (template.jacket_size) setJacketSize(template.jacket_size);
    if (template.pant_size) setPantSize(template.pant_size);
    if (template.vest_size) setVestSize(template.vest_size);
    if (template.jacket_measurements) setJacketMeasurements(template.jacket_measurements);
    if (template.pant_measurements) setPantMeasurements(template.pant_measurements);
    if (template.vest_measurements) setVestMeasurements(template.vest_measurements);
    if (template.jacket_styling) setJacketStyling(prev => ({ ...prev, ...template.jacket_styling }));
    if (template.pant_styling) setPantStyling(prev => ({ ...prev, ...template.pant_styling }));
    if (template.vest_styling) setVestStyling(prev => ({ ...prev, ...template.vest_styling }));
    if (template.shirt_styling) setShirtStyling(prev => ({ ...prev, ...template.shirt_styling }));
    if (template.body_posture?.jacket) setJacketPosture(template.body_posture.jacket);
    if (template.body_posture?.pant) setPantPosture(template.body_posture.pant);
    if (template.remarks?.jacket) setJacketRemarks(template.remarks.jacket);
    if (template.remarks?.pant) setPantRemarks(template.remarks.pant);
    if (template.remarks?.vest) setVestRemarks(template.remarks.vest);
    setNotes(template.notes || "");
    setShowLoadDialog(false);
    toast.success(`Loaded template: ${template.name}`);
  };

  // Save the current Quick Order form as an MTM order so the PDF / SMS /
  // Email actions can target it. Returns the saved order id, or "" if it
  // could not be saved (missing customer, etc.).
  const ensureSavedOrderId = async () => {
    if (lastSavedOrderId) return lastSavedOrderId;
    if (!selectedClient) {
      toast.error("Pick a customer before sending the order");
      return "";
    }
    try {
      const orderNumber = `QO-${Date.now().toString().slice(-8)}`;
      const orderPayload = {
        order_number: orderNumber,
        client_id: selectedClient.id,
        client_name: selectedClient.name,
        client_email: selectedClient.email || "",
        client_phone: selectedClient.phone || "",
        client_address: selectedClient.address || "",
        garment_type: garmentType,
        construction_type: "Quick Order",
        // Per-garment fabric/lining maps captured in the new selector
        fabric_by_garment: fabricByGarment,
        lining_by_garment: liningByGarment,
        fabric_code: fabricByGarment.jacket || fabricByGarment.overcoat || "",
        lining_code: liningByGarment.jacket || liningByGarment.overcoat || "",
        jacket_size: jacketSize,
        pant_size: pantSize,
        vest_size: vestSize,
        jacket_measurements: jacketMeasurements,
        pant_measurements: pantMeasurements,
        vest_measurements: vestMeasurements,
        jacket_styling: jacketStyling,
        pant_styling: pantStyling,
        vest_styling: vestStyling,
        shirt_styling: shirtStyling,
        notes: notes,
        status: "draft",
        price: 0,
        total: 0,
        order_date: new Date().toISOString().split("T")[0],
      };
      const res = await axios.post(`${API}/mtm-orders`, orderPayload);
      const newId = res?.data?.id || "";
      setLastSavedOrderId(newId);
      setLastSavedOrderNumber(orderNumber);
      toast.success(`Order ${orderNumber} saved`);
      return newId;
    } catch (e) {
      console.error("Quick Order save failed", e);
      toast.error(e?.response?.data?.detail || "Failed to save order");
      return "";
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm("Are you sure you want to delete this template?")) return;

    try {
      await axios.delete(`${API}/admin/quick-order/templates/${templateId}`);
      toast.success("Template deleted");
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const handleCopyToPOS = () => {
    const posData = {
      selected_client: selectedClient,
      garment_type: garmentType,
      jacket_size: jacketSize,
      pant_size: pantSize,
      vest_size: vestSize,
      jacket_measurements: jacketMeasurements,
      pant_measurements: pantMeasurements,
      vest_measurements: vestMeasurements,
      jacket_styling: jacketStyling,
      pant_styling: pantStyling,
      vest_styling: vestStyling,
      shirt_styling: shirtStyling,
      jacket_posture: jacketPosture,
      pant_posture: pantPosture,
      jacket_remarks: jacketRemarks,
      pant_remarks: pantRemarks,
      vest_remarks: vestRemarks,
      notes: notes,
      from_quick_order: true
    };
    
    sessionStorage.setItem("quickOrderData", JSON.stringify(posData));
    toast.success("Data ready for POS!");
    navigate("/admin/pos");
  };

  // Customer functions
  const filteredClients = useMemo(() => {
    if (!clientSearch) return clients.slice(0, 10);
    const search = clientSearch.toLowerCase();
    return clients.filter(c => 
      c.name?.toLowerCase().includes(search) || 
      c.email?.toLowerCase().includes(search) ||
      c.phone?.includes(search)
    ).slice(0, 10);
  }, [clients, clientSearch]);

  const selectClient = (client) => {
    setSelectedClient(client);
    setClientSearch("");
    toast.success(`Selected ${client.name}`);
  };

  const createNewClient = async () => {
    if (!newClientForm.name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    setCreatingClient(true);
    try {
      const res = await axios.post(`${API}/crm/clients`, {
        name: newClientForm.name,
        email: newClientForm.email || "",
        phone: newClientForm.phone || "",
        address: newClientForm.address || "",
        status: "active"
      });
      setSelectedClient(res.data);
      setClients([...clients, res.data]);
      setShowNewClientForm(false);
      setNewClientForm({ name: "", email: "", phone: "", address: "" });
      toast.success(`Created: ${res.data.name}`);
    } catch (error) {
      toast.error("Failed to create customer");
    } finally {
      setCreatingClient(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Apply jacket size from chart
  const applyJacketSize = (size) => {
    const sizeData = sizeCharts.jacket?.find(s => s.size === size);
    if (sizeData) {
      setJacketMeasurements({
        chest: { starting: sizeData.chest?.toString() || "", adjustment: 0 },
        stomach: { starting: sizeData.stomach?.toString() || "", adjustment: 0 },
        seat: { starting: sizeData.seat?.toString() || "", adjustment: 0 },
        shoulder: { starting: sizeData.shoulder?.toString() || "", adjustment: 0 },
        bicep: { starting: sizeData.bicep?.toString() || "", adjustment: 0 },
        right_sleeve_length: { starting: sizeData.sleeve_length?.toString() || "", adjustment: 0 },
        left_sleeve_length: { starting: sizeData.sleeve_length?.toString() || "", adjustment: 0 },
        back_length: { starting: sizeData.back_length?.toString() || "", adjustment: 0 },
        front_length: { starting: sizeData.front_length?.toString() || "", adjustment: 0 },
        right_cuff: { starting: sizeData.cuff?.toString() || "", adjustment: 0 },
        left_cuff: { starting: sizeData.cuff?.toString() || "", adjustment: 0 },
        button_stance: { starting: sizeData.button_stance?.toString() || "", adjustment: 0 }
      });
      setJacketSize(size);
      toast.success(`Jacket size ${size} loaded`);
    }
  };

  // Apply pant size from chart
  const applyPantSize = (size) => {
    const sizeData = sizeCharts.pant?.find(s => s.size === size);
    if (sizeData) {
      setPantMeasurements({
        waist: { starting: sizeData.waist?.toString() || "", adjustment: 0 },
        seat: { starting: sizeData.seat?.toString() || "", adjustment: 0 },
        rise: { starting: sizeData.urise?.toString() || "", adjustment: 0 },
        thigh: { starting: sizeData.thigh?.toString() || "", adjustment: 0 },
        right_pant_length: { starting: sizeData.pant_length?.toString() || "", adjustment: 0 },
        left_pant_length: { starting: sizeData.pant_length?.toString() || "", adjustment: 0 },
        knee: { starting: sizeData.knee?.toString() || "", adjustment: 0 },
        bottom: { starting: sizeData.bottom?.toString() || "", adjustment: 0 }
      });
      setPantSize(size);
      toast.success(`Pant size ${size} loaded`);
    }
  };

  // Apply vest size from chart
  const applyVestSize = (size) => {
    const sizeData = sizeCharts.vest?.find(s => s.size === size);
    if (sizeData) {
      setVestMeasurements({
        chest: { starting: sizeData.chest?.toString() || "", adjustment: 0 },
        stomach: { starting: sizeData.stomach?.toString() || "", adjustment: 0 },
        back_length: { starting: sizeData.back_length?.toString() || "", adjustment: 0 },
        front_length: { starting: sizeData.front_length?.toString() || "", adjustment: 0 },
        bottom: { starting: sizeData.bottom?.toString() || "", adjustment: 0 }
      });
      setVestSize(size);
      toast.success(`Vest size ${size} loaded`);
    }
  };

  const hasJacket = ["2-Piece Suit", "3-Piece Suit", "Sport Coat", "Overcoat", "Tuxedo"].includes(garmentType);
  const hasPants = ["2-Piece Suit", "3-Piece Suit", "Trousers", "Tuxedo"].includes(garmentType);
  const hasVest = ["3-Piece Suit", "Vest"].includes(garmentType);
  const hasShirt = ["Dress Shirt"].includes(garmentType);

  if (!isAuthenticated) return null;

  return (
    <div className="admin-screen min-h-screen pt-20 pb-12">
      {/* Header */}
      <section className="py-6 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button onClick={() => navigate("/admin")} className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-3 transition-colors text-sm" data-testid="back-to-dashboard-btn">
              <ArrowLeft size={14} /> Back to Dashboard
            </button>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                  <FileText size={20} className="text-white" />
                </div>
                <div>
                  <h1 className="font-serif text-xl sm:text-2xl tracking-tight">Quick Order</h1>
                  <p className="text-xs text-muted-foreground">Create and save measurement & style templates</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <MobileModeToggle />
                <LoadStylePresetButton
                  jacketStyling={jacketStyling}
                  setJacketStyling={setJacketStyling}
                  pantStyling={pantStyling}
                  setPantStyling={setPantStyling}
                  vestStyling={vestStyling}
                  setVestStyling={setVestStyling}
                  shirtStyling={shirtStyling}
                  setShirtStyling={setShirtStyling}
                  testId="qo-load-style-preset"
                />
                <Button variant="outline" onClick={() => setShowLoadDialog(true)} className="border-white/10 h-9 text-sm" data-testid="load-template-btn">
                  <Download size={14} className="mr-2" /> Load
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowMockupPreview(!showMockupPreview)} 
                  className={`border-white/10 h-9 text-sm ${showMockupPreview ? 'bg-amber-500/20 border-amber-500/50 text-amber-400' : ''}`}
                  disabled={!garmentType}
                  data-testid="style-preview-btn"
                >
                  <Wand2 size={14} className="mr-2" /> Style Preview
                </Button>
                <Button variant="outline" onClick={() => setShowSaveDialog(true)} className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 h-9 text-sm" data-testid="save-template-btn">
                  <Save size={14} className="mr-2" /> Save
                </Button>
                <Button onClick={handleCopyToPOS} className="bg-primary hover:bg-primary/90 h-9 text-sm" data-testid="copy-to-pos-btn">
                  <Copy size={14} className="mr-2" /> Copy to POS
                </Button>
                <OrderActions
                  orderId={lastSavedOrderId}
                  orderNumber={lastSavedOrderNumber}
                  defaultPhone={selectedClient?.phone || ""}
                  defaultEmail={selectedClient?.email || ""}
                  ensureSavedOrderId={ensureSavedOrderId}
                  variant="inline"
                  testIdPrefix="qo-actions"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-6">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8">
          {/* Make toggle */}
          <div className="bg-card border border-white/10 rounded-lg p-3 mb-4 flex items-center justify-end flex-wrap gap-3">
            <MakeToggle
              value={make}
              onChange={setMake}
              compact
            />
          </div>

          {/* Customer Selection */}
          <div className="bg-card border border-white/10 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <User size={16} className="text-amber-400" />
              <h3 className="text-sm font-medium">Select Customer</h3>
              <span className="text-xs text-slate-400">Choose an existing customer or create new</span>
            </div>
            
            {selectedClient ? (
              <div className="flex items-center gap-4 p-3 bg-[#111c2e] rounded-lg border border-amber-500/50">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 font-bold">
                  {selectedClient.name?.charAt(0)}
                </div>
                <div className="flex-1">
                  <p className="font-medium">{selectedClient.name}</p>
                  <p className="text-xs text-slate-400">{selectedClient.email || selectedClient.phone || 'No contact info'}</p>
                </div>
                <button onClick={() => setSelectedClient(null)} className="text-slate-400 hover:text-red-400">
                  <X size={18} />
                </button>
              </div>
            ) : showNewClientForm ? (
              <div className="p-4 bg-[#111c2e] rounded-lg border border-white/10">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium flex items-center gap-2"><UserPlus size={16} className="text-amber-400" />New Customer</h4>
                  <button onClick={() => setShowNewClientForm(false)} className="text-slate-400 hover:text-white"><X size={16} /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Name *</label>
                    <Input placeholder="Customer name" value={newClientForm.name} onChange={(e) => setNewClientForm({...newClientForm, name: e.target.value})} className="bg-[#0a1628] border-white/10 h-9 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Email</label>
                    <Input type="email" placeholder="email@example.com" value={newClientForm.email} onChange={(e) => setNewClientForm({...newClientForm, email: e.target.value})} className="bg-[#0a1628] border-white/10 h-9 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Phone</label>
                    <Input placeholder="Phone number" value={newClientForm.phone} onChange={(e) => setNewClientForm({...newClientForm, phone: e.target.value})} className="bg-[#0a1628] border-white/10 h-9 text-sm" />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={createNewClient} disabled={creatingClient || !newClientForm.name.trim()} className="w-full bg-amber-500 hover:bg-amber-600 h-9 text-sm">
                      {creatingClient ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
                      Create
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input placeholder="Search by name, email, or phone..." value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} className="pl-10 h-10 bg-[#111c2e] border-white/10" />
                </div>
                <Button onClick={() => setShowNewClientForm(true)} variant="outline" className="h-10 border-amber-500/50 text-amber-400">
                  <UserPlus size={16} className="mr-2" />New
                </Button>
              </div>
            )}
            
            {/* Search Results */}
            {!selectedClient && !showNewClientForm && clientSearch && (
              <div className="mt-2 max-h-[200px] overflow-y-auto space-y-1">
                {filteredClients.length > 0 ? filteredClients.map(client => (
                  <button key={client.id} onClick={() => selectClient(client)} className="w-full p-2 bg-[#111c2e] rounded-lg border border-white/10 hover:border-amber-500/50 text-left flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center font-bold text-xs">{client.name?.charAt(0)}</div>
                    <div className="flex-1">
                      <p className="font-medium">{client.name}</p>
                      <p className="text-xs text-slate-400">{client.email || client.phone}</p>
                    </div>
                    <ChevronRight size={16} className="text-slate-500" />
                  </button>
                )) : (
                  <p className="text-sm text-slate-400 p-2">No customers found</p>
                )}
              </div>
            )}
            
            {/* Quick select recent customers when no search */}
            {!selectedClient && !showNewClientForm && !clientSearch && clients.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {clients.slice(0, 5).map(client => (
                  <button key={client.id} onClick={() => selectClient(client)} className="px-3 py-1.5 bg-[#111c2e] rounded-lg border border-white/10 hover:border-amber-500/50 text-xs flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-slate-700 flex items-center justify-center font-bold text-[10px]">{client.name?.charAt(0)}</div>
                    {client.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Garment Type */}
          <div className="bg-card border border-white/10 rounded-lg p-4 mb-4">
            <div className="flex flex-wrap gap-2">
              {GARMENT_TYPES.map((type) => (
                <button key={type} onClick={() => setGarmentType(type)}
                  className={`px-3 py-1.5 rounded-lg border text-xs transition-all ${garmentType === type ? "border-primary bg-primary/10 text-primary" : "border-white/10 hover:border-white/20"}`}
                  data-testid={`garment-type-${type.toLowerCase().replace(/\s+/g, '-')}`}
                >{type}</button>
              ))}
            </div>
          </div>

          {/* Fabric Selection — per garment with "Match all" buttons */}
          <div className="mb-4">
            <GarmentFabricSection
              fabricByGarment={fabricByGarment}
              setFabricByGarment={setFabricByGarment}
              liningByGarment={liningByGarment}
              setLiningByGarment={(next) => {
                // Cascade global lining picks down into the per-garment
                // styling sub-sections so the LINING COLORS rows + Inner
                // Facing Piping in the Styles section auto-fill. Sleeve
                // lining and Inner Facing Piping follow Body Lining unless
                // the user has explicitly picked a different value — we
                // track the last cascaded value per field so manual
                // overrides stick across future Body Lining changes.
                const prev = liningByGarment || {};
                const tracker = liningCascadeRef.current;
                setLiningByGarment(next);

                const applyJacketLike = (code, fieldKey) => {
                  if (!code) return;
                  setJacketStyling((s) => {
                    const upd = { ...s, lining_color: code };
                    if (!s.sleeve_lining_color || s.sleeve_lining_color === tracker.sleeve) {
                      upd.sleeve_lining_color = code;
                      tracker.sleeve = code;
                    }
                    if (!s.inner_facing_piping || s.inner_facing_piping === tracker.facing) {
                      upd.inner_facing_piping = code;
                      tracker.facing = code;
                    }
                    return upd;
                  });
                };

                if (next.jacket && next.jacket !== prev.jacket) {
                  applyJacketLike(next.jacket, "jacket");
                }
                if (next.overcoat && next.overcoat !== prev.overcoat) {
                  applyJacketLike(next.overcoat, "overcoat");
                }
                if (next.pant && next.pant !== prev.pant) {
                  setPantStyling((s) => {
                    if (!s.pant_lining_color || s.pant_lining_color === tracker.pant) {
                      tracker.pant = next.pant;
                      return { ...s, pant_lining_color: next.pant };
                    }
                    return s;
                  });
                }
                if (next.vest && next.vest !== prev.vest) {
                  setVestStyling((s) => {
                    if (!s.vest_lining_color || s.vest_lining_color === tracker.vest) {
                      tracker.vest = next.vest;
                      return { ...s, vest_lining_color: next.vest };
                    }
                    return s;
                  });
                }
              }}
              garmentType={garmentType}
              buttonSourceLabel="Jacket"
              buttonSourceValue={jacketStyling?.button_style || ""}
              onMatchAllButtons={() => {
                const src = jacketStyling?.button_style;
                if (!src) return;
                // Cascade the chosen jacket button across every button slot
                // (jacket button_style, pant front button, vest front button,
                // shirt button) — with sticky-override: skip a follower whose
                // current value differs from the last cascaded source.
                const lastSrc = buttonMatchSourceRef.current;
                const maybeSet = (cur, key) => {
                  if (lastSrc !== null && cur[key] && cur[key] !== lastSrc) return cur;
                  return { ...cur, [key]: src };
                };
                setJacketStyling((s) => maybeSet(s, "button_style"));
                setPantStyling((s) => maybeSet(s, "pant_front_button_code"));
                setVestStyling((s) => maybeSet(s, "front_button_code"));
                setShirtStyling((s) => maybeSet(s, "button_style"));
                buttonMatchSourceRef.current = src;
              }}
              buttonByGarment={{
                jacket: jacketStyling?.button_style || "",
                pant: pantStyling?.pant_front_button_code || "",
                vest: vestStyling?.front_button_code || "",
                shirt: shirtStyling?.button_style || "",
                overcoat: jacketStyling?.button_style || "",
              }}
              onButtonChange={(garment, value) => {
                if (garment === "jacket" || garment === "overcoat") {
                  setJacketStyling((s) => ({ ...s, button_style: value }));
                } else if (garment === "pant") {
                  setPantStyling((s) => ({ ...s, pant_front_button_code: value }));
                } else if (garment === "vest") {
                  setVestStyling((s) => ({ ...s, front_button_code: value }));
                } else if (garment === "shirt") {
                  setShirtStyling((s) => ({ ...s, button_style: value }));
                }
              }}
            />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {/* Left Column - Jacket */}
            {hasJacket && (
              <div className="space-y-4">
                {/* Jacket Measurements */}
                <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                  <button onClick={() => toggleSection('jacketMeasurements')} className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition-colors">
                    <h3 className="text-sm font-medium flex items-center gap-2"><Ruler size={16} className="text-blue-400" /> Jacket Measurements</h3>
                    {expandedSections.jacketMeasurements ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  {expandedSections.jacketMeasurements && (
                    <div className="p-3 pt-0 border-t border-white/5">
                      {/* Model Size Selector */}
                      <div className="mb-3 pb-3 border-b border-white/10">
                        <label className="text-xs text-slate-400 block mb-1">Model Size</label>
                        <Select value={jacketSize} onValueChange={applyJacketSize}>
                          <SelectTrigger className="w-32 h-8 text-xs bg-background border-white/10" data-testid="jacket-size-select">
                            <SelectValue placeholder="Select Size" />
                          </SelectTrigger>
                          <SelectContent>
                            {sizeCharts.jacket?.map(s => (
                              <SelectItem key={s.size} value={s.size} className="text-xs">{s.size}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <table className="w-full">
                        <thead><tr className="text-xs text-muted-foreground"><th className="text-left py-1 px-2">Measure</th><th className="text-center py-1">Start</th><th className="text-center py-1">Adj</th><th className="text-center py-1">Final</th></tr></thead>
                        <tbody>
                          {Object.entries(jacketMeasurements).map(([key, value]) => (
                            <MeasurementRow key={key} fieldKey={key} allMeasurements={jacketMeasurements} label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} startingValue={value.starting} adjustment={value.adjustment}
                              onStartingChange={(val) => setJacketMeasurements(prev => ({ ...prev, [key]: { ...prev[key], starting: val } }))}
                              onAdjustmentChange={(adj) => setJacketMeasurements(prev => ({ ...prev, [key]: { ...prev[key], adjustment: adj } }))} />
                          ))}
                        </tbody>
                      </table>
                      {/* Jacket Notes */}
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <Textarea value={jacketRemarks} onChange={(e) => setJacketRemarks(e.target.value)} placeholder="Jacket notes..." className="bg-background border-white/10 h-16 text-xs resize-none" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Jacket Posture */}
                <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                  <button onClick={() => toggleSection('jacketPosture')} className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition-colors">
                    <h3 className="text-sm font-medium flex items-center gap-2"><User size={16} className="text-cyan-400" /> Body Posture (Jacket)</h3>
                    {expandedSections.jacketPosture ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  {expandedSections.jacketPosture && (
                    <div className="p-3 pt-0 border-t border-white/5 space-y-1">
                      <PostureSelect label="Back Body" value={jacketPosture.back_body} onChange={(v) => setJacketPosture({...jacketPosture, back_body: v})} options={BACK_BODY_OPTIONS} />
                      <PostureSelect label="Back Posture" value={jacketPosture.back_posture} onChange={(v) => setJacketPosture({...jacketPosture, back_posture: v})} options={BACK_POSTURE_OPTIONS} />
                      <PostureSelect label="Shoulder Blade" value={jacketPosture.shoulder_blade} onChange={(v) => setJacketPosture({...jacketPosture, shoulder_blade: v})} options={SHOULDER_BLADE_OPTIONS} />
                      <PostureSelect label="Neck/Head" value={jacketPosture.neck} onChange={(v) => setJacketPosture({...jacketPosture, neck: v})} options={NECK_OPTIONS} />
                      <PostureSelect label="Lower Collar" value={jacketPosture.lower_collar} onChange={(v) => setJacketPosture({...jacketPosture, lower_collar: v})} options={LOWER_COLLAR_OPTIONS} />
                      <PostureSelect label="Chest" value={jacketPosture.chest} onChange={(v) => setJacketPosture({...jacketPosture, chest: v})} options={CHEST_OPTIONS} />
                      <PostureSelect label="Arm" value={jacketPosture.arm} onChange={(v) => setJacketPosture({...jacketPosture, arm: v})} options={ARM_OPTIONS} />
                      <PostureSelect label="Waist Height" value={jacketPosture.waist_height} onChange={(v) => setJacketPosture({...jacketPosture, waist_height: v})} options={WAIST_HEIGHT_OPTIONS} />
                      <PostureSelect label="Left Shoulder" value={jacketPosture.left_shoulder} onChange={(v) => setJacketPosture({...jacketPosture, left_shoulder: v})} options={SHOULDER_OPTIONS} />
                      <PostureSelect label="Right Shoulder" value={jacketPosture.right_shoulder} onChange={(v) => setJacketPosture({...jacketPosture, right_shoulder: v})} options={SHOULDER_OPTIONS} />
                    </div>
                  )}
                </div>

                {/* Jacket Styling */}
                <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                  <button onClick={() => toggleSection('jacketStyling')} className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition-colors">
                    <h3 className="text-sm font-medium flex items-center gap-2"><Scissors size={16} className="text-amber-400" /> Jacket Styling</h3>
                    {expandedSections.jacketStyling ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  {expandedSections.jacketStyling && (
                    <div className="p-3 pt-0 border-t border-white/5">
                      <div className="flex justify-end pb-2 -mt-1">
                        <LoadStylePresetButton
                          section="jacket"
                          jacketStyling={jacketStyling}
                          setJacketStyling={setJacketStyling}
                          testId="qo-load-jacket-style"
                        />
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        <StyleSelect label="Canvas Construction" value={jacketStyling.canvas_construction} onChange={(v) => setJacketStyling({...jacketStyling, canvas_construction: v})} options={JACKET_CANVAS_CONSTRUCTIONS} codePrefix="canvas_construction" />
                        <StyleSelect label="Front Closure" value={jacketStyling.front_closure} onChange={(v) => setJacketStyling({...jacketStyling, front_closure: v})} options={JACKET_FRONT_CLOSURES} codePrefix="front_closure" />
                        <StyleSelect label="Lapel Type" value={jacketStyling.lapel_type} onChange={(v) => setJacketStyling({...jacketStyling, lapel_type: v})} options={LAPEL_TYPES} codePrefix="lapel_type" iconKind="lapel" />
                        <StyleSelect label="Lapel Width" value={jacketStyling.lapel_width} onChange={(v) => setJacketStyling({...jacketStyling, lapel_width: v})} options={LAPEL_WIDTHS} codePrefix="lapel_width" />
                        <StyleSelect label="Gorge Line" value={jacketStyling.gorge_line} onChange={(v) => setJacketStyling({...jacketStyling, gorge_line: v})} options={GORGE_LINES} />
                        <StyleSelect label="Waist Pocket" value={jacketStyling.waist_pocket_type} onChange={(v) => setJacketStyling({...jacketStyling, waist_pocket_type: v})} options={WAIST_POCKET_TYPES} codePrefix="waist_pocket_type" iconKind="pocket" />
                        <StyleSelect label="Pocket Angle" value={jacketStyling.waist_pocket_angle} onChange={(v) => setJacketStyling({...jacketStyling, waist_pocket_angle: v})} options={WAIST_POCKET_ANGLES} codePrefix="waist_pocket_angle" />
                        <StyleSelect label="Ticket Pocket" value={jacketStyling.ticket_pocket} onChange={(v) => setJacketStyling({...jacketStyling, ticket_pocket: v})} options={TICKET_POCKETS} codePrefix="ticket_pocket" />
                        <StyleSelect label="Chest Pocket" value={jacketStyling.chest_pocket} onChange={(v) => setJacketStyling({...jacketStyling, chest_pocket: v})} options={CHEST_POCKETS} codePrefix="chest_pocket" />
                        <StyleSelect label="Exterior Stitch" value={jacketStyling.exterior_stitch} onChange={(v) => setJacketStyling({...jacketStyling, exterior_stitch: v})} options={EXTERIOR_STITCHES} codePrefix="exterior_stitch" />
                        <StyleSelect label="Sleeve Buttons" value={jacketStyling.sleeve_buttonholes} onChange={(v) => setJacketStyling({...jacketStyling, sleeve_buttonholes: v})} options={SLEEVE_BUTTONHOLES} codePrefix="sleeve_buttons" />
                        <StyleSelect label="Vent" value={jacketStyling.vent} onChange={(v) => setJacketStyling({...jacketStyling, vent: v})} options={VENTS} codePrefix="vent" iconKind="vent" />
                        <StyleSelect label="Lining Style" value={jacketStyling.lining_style} onChange={(v) => setJacketStyling({...jacketStyling, lining_style: v})} options={[]} codePrefix="lining_style" />
                        <StyleSelect label="Lapel Buttonhole" value={jacketStyling.lapel_buttonhole} onChange={(v) => setJacketStyling({...jacketStyling, lapel_buttonhole: v})} options={LAPEL_BUTTONHOLES} codePrefix="lapel_buttonhole" />
                        <StyleSelect label="Chest Pocket Posture" value={jacketStyling.chest_pocket_posture} onChange={(v) => setJacketStyling({...jacketStyling, chest_pocket_posture: v})} options={CHEST_POCKET_POSTURES} codePrefix="chest_pocket_posture" />
                        <StyleSelect label="Pocket Flap Width" value={jacketStyling.lower_pocket_flap_width} onChange={(v) => setJacketStyling({...jacketStyling, lower_pocket_flap_width: v})} options={LOWER_POCKET_FLAP_WIDTHS} codePrefix="lower_pocket_flap_width" />
                        <StyleSelect label="Ticket Posture" value={jacketStyling.ticket_pocket_posture} onChange={(v) => setJacketStyling({...jacketStyling, ticket_pocket_posture: v})} options={TICKET_POCKET_POSTURES} codePrefix="ticket_pocket_posture" />
                        <StyleSelect label="Vent Length" value={jacketStyling.back_vent_length} onChange={(v) => setJacketStyling({...jacketStyling, back_vent_length: v})} options={BACK_VENT_LENGTHS} codePrefix="back_vent_length" />
                      </div>
                      <LiningMatchRow
                        jacketStyling={jacketStyling}
                        setJacketStyling={setJacketStyling}
                        pantStyling={pantStyling}
                        setPantStyling={setPantStyling}
                        vestStyling={vestStyling}
                        setVestStyling={setVestStyling}
                        testId="qo-jacket-lining-match"
                      />
                      <InnerFacingPipingRow
                        value={jacketStyling.inner_facing_piping}
                        onChange={(v) => setJacketStyling({ ...jacketStyling, inner_facing_piping: v })}
                        testId="qo-jacket-inner-facing-piping"
                      />

                      {/* Monogram */}
                      <div className="mt-3 pt-3 border-t border-white/5">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">Monogram</p>
                        <div className="max-w-xs">
                          <label className="text-[10px] text-slate-500 block mb-0.5">Under Collar Monogram</label>
                          <Input
                            value={jacketStyling.under_collar_monogram || ""}
                            onChange={(e) => setJacketStyling({...jacketStyling, under_collar_monogram: e.target.value})}
                            placeholder="Initials..."
                            className="bg-background border-white/10 h-8 text-xs"
                            data-testid="qo-jacket-monogram-input"
                          />
                        </div>
                      </div>

                      {/* Postures */}
                      <div className="mt-3 pt-3 border-t border-white/5" data-testid="qo-jacket-postures">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">Postures</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                          <StyleSelect label="Armhole Adj" value={jacketStyling.armhole_adjustment} onChange={(v) => setJacketStyling({...jacketStyling, armhole_adjustment: v})} options={ARMHOLE_ADJUSTMENTS} codePrefix="armhole_adjustment" />
                          <ShoulderPosturePair styling={jacketStyling} setStyling={setJacketStyling} options={SHOULDER_POSTURE_ADJUSTMENTS} testId="qo-shoulder-posture" />
                          <StyleSelect label="Slope Degree (溜肩程度)" value={jacketStyling.slope_shoulder_degree} onChange={(v) => setJacketStyling({...jacketStyling, slope_shoulder_degree: v})} options={[]} codePrefix="slope_shoulder_degree" />
                          <StyleSelect label="Back Width Adj" value={jacketStyling.back_width_adjustment} onChange={(v) => setJacketStyling({...jacketStyling, back_width_adjustment: v})} options={[]} codePrefix="back_width_adjustment" />
                          <StyleSelect label="Back Collar Adj (Lower collar)" value={jacketStyling.back_collar_adjustment} onChange={(v) => setJacketStyling({...jacketStyling, back_collar_adjustment: v})} options={[]} codePrefix="back_collar_adjustment" />
                          <StyleSelect label="Front Chest Adj" value={jacketStyling.front_chest_adjustment} onChange={(v) => setJacketStyling({...jacketStyling, front_chest_adjustment: v})} options={[]} codePrefix="front_chest_adjustment" />
                          <StyleSelect label="Front Waist Adj (前中腰围)" value={jacketStyling.front_waist_adjustment} onChange={(v) => setJacketStyling({...jacketStyling, front_waist_adjustment: v})} options={[]} codePrefix="front_waist_adjustment" />
                          <ShoulderPadPair styling={jacketStyling} setStyling={setJacketStyling} StyleSelectComponent={StyleSelect} testId="qo-shoulder-pad" />
                        </div>
                      </div>

                      <StyleSubSection title="Jacket Allowances" testId="qo-jacket-allowances">
                        <StyleSelect label="Sleeve Seam Adjustment" value={jacketStyling.sleeve_seam_adjustment} onChange={(v) => setJacketStyling({...jacketStyling, sleeve_seam_adjustment: v})} options={[]} codePrefix="sleeve_seam_adjustment" />
                        <StyleSelect label="Pocket Seam Fabric" value={jacketStyling.pocket_seam_fabric} onChange={(v) => setJacketStyling({...jacketStyling, pocket_seam_fabric: v})} options={[]} codePrefix="pocket_seam_fabric" />
                        <StyleSelect label="Back Side Seam" value={jacketStyling.back_side_seam} onChange={(v) => setJacketStyling({...jacketStyling, back_side_seam: v})} options={[]} codePrefix="back_side_seam" />
                      </StyleSubSection>
                      <div className="mt-3 pt-3 border-t border-white/5" data-testid="qo-jacket-button-style-section">
                        <div className="flex items-center gap-2 mb-2">
                          <p className="text-[10px] uppercase tracking-wider text-slate-400">Button Style</p>
                          <code className="text-[10px] text-amber-400/80 font-mono">C-0638</code>
                          <span className="text-[10px] text-slate-500 italic">(Customer appointed button color)</span>
                        </div>
                        <div className="max-w-sm">
                          <ButtonStyleSelect label="" category="suit" value={jacketStyling.button_style} onChange={(v) => setJacketStyling({...jacketStyling, button_style: v})} testId="qo-jacket-button-style" />
                        </div>
                      </div>

                      <AutoStyleFields
                        section="jacket"
                        styling={jacketStyling}
                        setStyling={setJacketStyling}
                        excludeFields={[
                          "body_style","canvas_construction","front_closure","lapel_type","lapel_width","gorge_line",
                          "shoulder_pad","shoulder_pad_left","shoulder_pad_right",
                          "shoulder_posture","shoulder_posture_left","shoulder_posture_right",
                          "waist_pocket_type","waist_pocket_angle","ticket_pocket","chest_pocket",
                          "button_style","exterior_stitch","sleeve_buttons","sleeve_slit_option","vent",
                          "lining_style","lapel_buttonhole","chest_pocket_posture","lower_pocket_flap_width",
                          "ticket_pocket_posture","back_vent_length","armhole_adjustment",
                          "slope_shoulder_degree","back_width_adjustment",
                          "back_collar_adjustment","lining_color","sleeve_lining_color","inner_facing_piping",
                          "front_chest_adjustment","front_waist_adjustment",
                          "under_collar_monogram",
                          "sleeve_seam_adjustment","pocket_seam_fabric","back_side_seam",
                          "front_button_code","button_style_code",
                        ]}
                        hideFieldsMatching={(garmentType === "Tuxedo" || (garmentType || "").toLowerCase().includes("tux")) ? [] : ["tuxedo","tux"]}
                        testIdPrefix="qo-jacket-auto-style"
                      />
                      <CustomStyleCodes
                        bucket="jacket"
                        value={jacketStyling.custom_codes || []}
                        onChange={(v) => setJacketStyling({ ...jacketStyling, custom_codes: v })}
                        testId="qo-jacket-custom-codes"
                      />
                      <div className="mt-3">
                        <Textarea value={jacketRemarks} onChange={(e) => setJacketRemarks(e.target.value)} placeholder="Jacket notes..." className="bg-background border-white/10 h-16 text-xs resize-none" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Right Column - Pants & Vest */}
            <div className="space-y-4">
              {/* Pant Measurements */}
              {hasPants && (
                <>
                  <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                    <button onClick={() => toggleSection('pantMeasurements')} className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition-colors">
                      <h3 className="text-sm font-medium flex items-center gap-2"><Ruler size={16} className="text-amber-400" /> Trouser Measurements</h3>
                      {expandedSections.pantMeasurements ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    {expandedSections.pantMeasurements && (
                      <div className="p-3 pt-0 border-t border-white/5">
                        {/* Model Size Selector */}
                        <div className="mb-3 pb-3 border-b border-white/10">
                          <label className="text-xs text-slate-400 block mb-1">Model Size</label>
                          <Select value={pantSize} onValueChange={applyPantSize}>
                            <SelectTrigger className="w-32 h-8 text-xs bg-background border-white/10" data-testid="pant-size-select">
                              <SelectValue placeholder="Select Size" />
                            </SelectTrigger>
                            <SelectContent>
                              {sizeCharts.pant?.map(s => (
                                <SelectItem key={s.size} value={s.size} className="text-xs">{s.size}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <table className="w-full">
                          <thead><tr className="text-xs text-muted-foreground"><th className="text-left py-1 px-2">Measure</th><th className="text-center py-1">Start</th><th className="text-center py-1">Adj</th><th className="text-center py-1">Final</th></tr></thead>
                          <tbody>
                            {Object.entries(pantMeasurements).map(([key, value]) => (
                              <MeasurementRow key={key} fieldKey={key} allMeasurements={pantMeasurements} label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} startingValue={value.starting} adjustment={value.adjustment}
                                onStartingChange={(val) => setPantMeasurements(prev => ({ ...prev, [key]: { ...prev[key], starting: val } }))}
                                onAdjustmentChange={(adj) => setPantMeasurements(prev => ({ ...prev, [key]: { ...prev[key], adjustment: adj } }))} />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Pant Posture */}
                  <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                    <button onClick={() => toggleSection('pantPosture')} className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition-colors">
                      <h3 className="text-sm font-medium flex items-center gap-2"><User size={16} className="text-orange-400" /> Posture Adjustments</h3>
                      {expandedSections.pantPosture ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    {expandedSections.pantPosture && (
                      <div className="p-3 pt-0 border-t border-white/5 space-y-1">
                        <PostureSelect label="Front Rise" value={pantPosture.front_rise} onChange={(v) => setPantPosture({...pantPosture, front_rise: v})} options={RISE_POSTURE_OPTIONS} />
                        <PostureSelect label="Back Rise" value={pantPosture.back_rise} onChange={(v) => setPantPosture({...pantPosture, back_rise: v})} options={RISE_POSTURE_OPTIONS} />
                      </div>
                    )}
                  </div>

                  {/* Trouser Notes */}
                  <div className="bg-card border border-white/10 rounded-lg p-3">
                    <Textarea value={pantRemarks} onChange={(e) => setPantRemarks(e.target.value)} placeholder="Trouser notes..." className="bg-background border-white/10 h-16 text-xs resize-none" />
                  </div>
                  <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                    <button onClick={() => toggleSection('pantStyling')} className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition-colors">
                      <h3 className="text-sm font-medium flex items-center gap-2"><Scissors size={16} className="text-blue-400" /> Trouser Styling</h3>
                      {expandedSections.pantStyling ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    {expandedSections.pantStyling && (
                      <div className="p-3 pt-0 border-t border-white/5">
                        <div className="flex justify-end gap-2 pb-2 -mt-1">
                          <PantTuxedoToggle
                            pantStyling={pantStyling}
                            setPantStyling={setPantStyling}
                            testId="qo-pant-tuxedo-toggle"
                          />
                          <LoadStylePresetButton
                            section="pant"
                            pantStyling={pantStyling}
                            setPantStyling={setPantStyling}
                            testId="qo-load-pant-style"
                          />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          <StyleSelect label="Body Style" value={pantStyling.body_style} onChange={(v) => setPantStyling({...pantStyling, body_style: v})} options={PANT_BODY_STYLES} codePrefix="pant_body_style" />
                          <StyleSelect label="Front Style" value={pantStyling.front_style} onChange={(v) => setPantStyling({...pantStyling, front_style: v})} options={PANT_FRONT_STYLES} codePrefix="pant_front_style" />
                          <StyleSelect label="Front Pocket" value={pantStyling.front_pocket} onChange={(v) => setPantStyling({...pantStyling, front_pocket: v})} options={FRONT_POCKETS} codePrefix="pant_front_pocket" />
                          <StyleSelect label="Back Pocket" value={pantStyling.back_pocket_style} onChange={(v) => setPantStyling({...pantStyling, back_pocket_style: v})} options={BACK_POCKET_STYLES} codePrefix="pant_back_pocket_style" />
                          <StyleSelect label="Back Closure" value={pantStyling.back_pocket_closure} onChange={(v) => setPantStyling({...pantStyling, back_pocket_closure: v})} options={BACK_POCKET_CLOSURES} codePrefix="pant_back_pocket_closure" />
                          <StyleSelect label="Bottom Finish" value={pantStyling.bottom_finishing} onChange={(v) => setPantStyling({...pantStyling, bottom_finishing: v})} options={BOTTOM_FINISHINGS} codePrefix="pant_bottom_finishing" iconKind="cuff" />
                          <StyleSelect label="Front Closure" value={pantStyling.front_closure} onChange={(v) => setPantStyling({...pantStyling, front_closure: v})} options={FRONT_CLOSURES_PANT} codePrefix="pant_front_closure" />
                          <StyleSelect label="Waistband Style" value={pantStyling.waistband_style} onChange={(v) => setPantStyling({...pantStyling, waistband_style: v})} options={WAISTBAND_STYLES} codePrefix="pant_waistband_style" />
                          <StyleSelect label="Waistband Height" value={pantStyling.waistband_width} onChange={(v) => setPantStyling({...pantStyling, waistband_width: v})} options={WAISTBAND_WIDTHS} codePrefix="pant_waistband_width" />
                          <StyleSelect label="Pleat Depth" value={pantStyling.pleat_depth} onChange={(v) => setPantStyling({...pantStyling, pleat_depth: v})} options={PANT_PLEAT_DEPTHS} codePrefix="pant_pleat_depth" />
                          <StyleSelect label="Watch Pocket" value={pantStyling.watch_pocket} onChange={(v) => setPantStyling({...pantStyling, watch_pocket: v})} options={WATCH_POCKETS} codePrefix="pant_watch_pocket" />
                          <StyleSelect label="Inner Watch Pocket" value={pantStyling.inner_watch_pocket} onChange={(v) => setPantStyling({...pantStyling, inner_watch_pocket: v})} options={INNER_WATCH_POCKETS} codePrefix="pant_inner_watch_pocket" />
                          <StyleSelect label="Front Pocket Bartack" value={pantStyling.front_pocket_bartack} onChange={(v) => setPantStyling({...pantStyling, front_pocket_bartack: v})} options={FRONT_POCKET_BARTACKS} codePrefix="pant_front_pocket_bartack" />
                          <StyleSelect label="Front Pocket Stitch" value={pantStyling.front_pocket_stitch} onChange={(v) => setPantStyling({...pantStyling, front_pocket_stitch: v})} options={FRONT_POCKET_STITCHES} codePrefix="pant_front_pocket_stitch" />
                        </div>
                        <SectionLiningRow
                          value={pantStyling.pant_lining_color}
                          onChange={(v) => setPantStyling({ ...pantStyling, pant_lining_color: v })}
                          label="Pant Lining"
                          testId="qo-pant-lining"
                        />
                        <PantFrontButtonRow
                          pantStyling={pantStyling}
                          setPantStyling={setPantStyling}
                          testId="qo-pant-front-button"
                        />
                        <StyleSubSection title="Pant Allowances" testId="qo-pant-allowances">
                          <StyleSelect label="Outer Seam Allowance" value={pantStyling.pant_outer_seam_allowance} onChange={(v) => setPantStyling({ ...pantStyling, pant_outer_seam_allowance: v })} options={[]} codePrefix="pant_outer_seam_allowance" />
                          <StyleSelect label="Seam Requirement" value={pantStyling.pant_seam_requirement} onChange={(v) => setPantStyling({ ...pantStyling, pant_seam_requirement: v })} options={[]} codePrefix="pant_seam_requirement" />
                        </StyleSubSection>
                        {isPantTuxedoActive(pantStyling) && (
                        <StyleSubSection title="Pant Satin Details" testId="qo-pant-satin-details">
                          <StyleSelect label="Waistband Fabric" value={pantStyling.pant_waistband_fabric} onChange={(v) => setPantStyling({ ...pantStyling, pant_waistband_fabric: v })} options={[]} codePrefix="pant_waistband_fabric" />
                          <StyleSelect label="Satin Position" value={pantStyling.pant_satin_position} onChange={(v) => setPantStyling({ ...pantStyling, pant_satin_position: v })} options={[]} codePrefix="pant_satin_position" />
                          <StyleSelect label="Back Pocket Besom Fabric" value={pantStyling.pant_back_pocket_besom_fabric} onChange={(v) => setPantStyling({ ...pantStyling, pant_back_pocket_besom_fabric: v })} options={[]} codePrefix="pant_back_pocket_besom_fabric" />
                          <StyleSelect label="Front Pocket Opening" value={pantStyling.pant_front_pocket_opening} onChange={(v) => setPantStyling({ ...pantStyling, pant_front_pocket_opening: v })} options={[]} codePrefix="pant_front_pocket_opening" />
                          <StyleSelect label="Satin Fabric Notice" value={pantStyling.pant_satin_fabric_notice} onChange={(v) => setPantStyling({ ...pantStyling, pant_satin_fabric_notice: v })} options={[]} codePrefix="pant_satin_fabric_notice" />
                        </StyleSubSection>
                        )}
                        <AutoStyleFields
                          section="pant"
                          styling={pantStyling}
                          setStyling={setPantStyling}
                          excludeFields={[
                            "pant_body_style","pant_front_style","pant_front_pocket",
                            "pant_back_pocket_style","pant_back_pocket_closure",
                            "pant_bottom_finishing","pant_front_closure","pant_waistband_style",
                            "pant_waistband_width","pant_pleat_depth","pant_watch_pocket",
                            "pant_inner_watch_pocket","pant_front_pocket_bartack",
                            "pant_front_pocket_stitch","pant_lining_color",
                            "pant_belt_loop_style",
                            "pant_outer_seam_allowance","pant_seam_requirement",
                            "pant_waistband_fabric","pant_satin_position",
                            "pant_back_pocket_besom_fabric","pant_front_pocket_opening",
                            "pant_satin_fabric_notice",
                          ]}
                          testIdPrefix="qo-pant-auto-style"
                        />
                        <CustomStyleCodes
                          bucket="pant"
                          value={pantStyling.custom_codes || []}
                          onChange={(v) => setPantStyling({ ...pantStyling, custom_codes: v })}
                          testId="qo-pant-custom-codes"
                        />
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Vest */}
              {hasVest && (
                <>
                  <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                    <button onClick={() => toggleSection('vestMeasurements')} className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition-colors">
                      <h3 className="text-sm font-medium flex items-center gap-2"><Ruler size={16} className="text-purple-400" /> Vest Measurements</h3>
                      {expandedSections.vestMeasurements ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    {expandedSections.vestMeasurements && (
                      <div className="p-3 pt-0 border-t border-white/5">
                        {/* Model Size Selector */}
                        <div className="mb-3 pb-3 border-b border-white/10">
                          <label className="text-xs text-slate-400 block mb-1">Model Size</label>
                          <Select value={vestSize} onValueChange={applyVestSize}>
                            <SelectTrigger className="w-32 h-8 text-xs bg-background border-white/10" data-testid="vest-size-select">
                              <SelectValue placeholder="Select Size" />
                            </SelectTrigger>
                            <SelectContent>
                              {sizeCharts.vest?.map(s => (
                                <SelectItem key={s.size} value={s.size} className="text-xs">{s.size}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <table className="w-full">
                          <thead><tr className="text-xs text-muted-foreground"><th className="text-left py-1 px-2">Measure</th><th className="text-center py-1">Start</th><th className="text-center py-1">Adj</th><th className="text-center py-1">Final</th></tr></thead>
                          <tbody>
                            {Object.entries(vestMeasurements).map(([key, value]) => (
                              <MeasurementRow key={key} fieldKey={key} allMeasurements={vestMeasurements} label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} startingValue={value.starting} adjustment={value.adjustment}
                                onStartingChange={(val) => setVestMeasurements(prev => ({ ...prev, [key]: { ...prev[key], starting: val } }))}
                                onAdjustmentChange={(adj) => setVestMeasurements(prev => ({ ...prev, [key]: { ...prev[key], adjustment: adj } }))} />
                            ))}
                          </tbody>
                        </table>
                        {/* Vest Notes */}
                        <div className="mt-3 pt-3 border-t border-white/10">
                          <Textarea value={vestRemarks} onChange={(e) => setVestRemarks(e.target.value)} placeholder="Vest notes..." className="bg-background border-white/10 h-16 text-xs resize-none" />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                    <button onClick={() => toggleSection('vestStyling')} className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition-colors">
                      <h3 className="text-sm font-medium flex items-center gap-2"><Scissors size={16} className="text-purple-400" /> Vest Styling</h3>
                      {expandedSections.vestStyling ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    {expandedSections.vestStyling && (
                      <div className="p-3 pt-0 border-t border-white/5">
                        <div className="flex justify-end pb-2 -mt-1">
                          <LoadStylePresetButton
                            section="vest"
                            vestStyling={vestStyling}
                            setVestStyling={setVestStyling}
                            testId="qo-load-vest-style"
                          />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          <StyleSelect label="Lapel" value={vestStyling.lapel} onChange={(v) => setVestStyling({...vestStyling, lapel: v})} options={VEST_LAPELS} codePrefix="vest_lapel" />
                          <StyleSelect label="Pocket Type" value={vestStyling.pocket_type} onChange={(v) => setVestStyling({...vestStyling, pocket_type: v})} options={VEST_POCKET_TYPES} codePrefix="vest_pocket_type" />
                          <StyleSelect label="Pocket Number" value={vestStyling.pocket_number} onChange={(v) => setVestStyling({...vestStyling, pocket_number: v})} options={VEST_POCKET_NUMBERS} codePrefix="vest_pocket_number" />
                          <StyleSelect label="Back" value={vestStyling.back} onChange={(v) => setVestStyling({...vestStyling, back: v})} options={VEST_BACKS} codePrefix="vest_back" />
                          <StyleSelect label="Back Belt" value={vestStyling.back_belt} onChange={(v) => setVestStyling({...vestStyling, back_belt: v})} options={[]} codePrefix="vest_back_belt" />
                        </div>
                        <div className="mt-3 pt-3 border-t border-white/5" data-testid="qo-vest-front-button-section">
                          <div className="flex items-center gap-2 mb-2">
                            <p className="text-[10px] uppercase tracking-wider text-slate-400">Front Button</p>
                            <code className="text-[10px] text-amber-400/80 font-mono">V-4240</code>
                            <span className="text-[10px] text-slate-500 italic">(button — customer appointed)</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <ButtonStyleSelect label="" category="suit" value={vestStyling.front_button_code} onChange={(v) => setVestStyling({...vestStyling, front_button_code: v})} testId="qo-vest-front-button" />
                            <StyleSelect label="" value={vestStyling.front_closure} onChange={(v) => setVestStyling({...vestStyling, front_closure: v})} options={VEST_FRONT_CLOSURES} codePrefix="vest_front_closure" />
                          </div>
                        </div>
                        <SectionLiningRow
                          value={vestStyling.vest_lining_color}
                          onChange={(v) => setVestStyling({
                            ...vestStyling,
                            vest_lining_color: v,
                            vest_inner_fabric: v ? "customer appointed lining for inner part" : vestStyling.vest_inner_fabric,
                          })}
                          label="Vest Lining"
                          testId="qo-vest-lining"
                        />
                        <AutoStyleFields
                          section="vest"
                          styling={vestStyling}
                          setStyling={setVestStyling}
                          excludeFields={[
                            "vest_lapel","vest_front_closure","vest_pocket_type",
                            "vest_pocket_number","vest_back","vest_back_belt","vest_lining_color",
                            "vest_inner_fabric","front_button_code",
                          ]}
                          testIdPrefix="qo-vest-auto-style"
                        />
                        <CustomStyleCodes
                          bucket="vest"
                          value={vestStyling.custom_codes || []}
                          onChange={(v) => setVestStyling({ ...vestStyling, custom_codes: v })}
                          testId="qo-vest-custom-codes"
                        />
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Shirt Measurements */}
              {hasShirt && (
                <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                  <button onClick={() => toggleSection('shirtMeasurements')} className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition-colors">
                    <h3 className="text-sm font-medium flex items-center gap-2"><Shirt size={16} className="text-rose-400" /> Shirt Measurements</h3>
                    {expandedSections.shirtMeasurements ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  {expandedSections.shirtMeasurements && (
                    <div className="p-3 pt-0 border-t border-white/5">
                      {/* Model Size Selector */}
                      <div className="mb-3 pb-3 border-b border-white/10">
                        <label className="text-xs text-slate-400 block mb-1">Model Size</label>
                        <Select value={shirtSize} onValueChange={(val) => {
                          setShirtSize(val);
                          const chart = sizeCharts.shirt?.find(s => s.size === val);
                          if (chart) {
                            const newMeas = {};
                            Object.keys(shirtMeasurements).forEach(key => {
                              const startVal = chart[key] || chart[key.replace('right_', '').replace('left_', '')] || "";
                              newMeas[key] = { starting: startVal, adjustment: 0 };
                            });
                            setShirtMeasurements(newMeas);
                          }
                        }}>
                          <SelectTrigger className="w-32 h-8 text-xs bg-background border-white/10" data-testid="shirt-size-select">
                            <SelectValue placeholder="Select Size" />
                          </SelectTrigger>
                          <SelectContent>
                            {sizeCharts.shirt?.map(s => (
                              <SelectItem key={s.size} value={s.size} className="text-xs">{s.size}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <table className="w-full">
                        <thead><tr className="text-xs text-muted-foreground"><th className="text-left py-1 px-2">Measure</th><th className="text-center py-1">Start</th><th className="text-center py-1">Adj</th><th className="text-center py-1">Final</th></tr></thead>
                        <tbody>
                          {Object.entries(shirtMeasurements).map(([key, value]) => (
                            <MeasurementRow key={key} fieldKey={key} allMeasurements={shirtMeasurements} label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} startingValue={value.starting} adjustment={value.adjustment}
                              onStartingChange={(val) => setShirtMeasurements(prev => ({ ...prev, [key]: { ...prev[key], starting: val } }))}
                              onAdjustmentChange={(adj) => setShirtMeasurements(prev => ({ ...prev, [key]: { ...prev[key], adjustment: adj } }))} />
                          ))}
                        </tbody>
                      </table>
                      {/* Shirt Notes */}
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <Textarea value={shirtRemarks} onChange={(e) => setShirtRemarks(e.target.value)} placeholder="Shirt notes..." className="bg-background border-white/10 h-16 text-xs resize-none" />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {hasShirt && (
                <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                  <button onClick={() => toggleSection('shirtStyling')} className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition-colors">
                    <h3 className="text-sm font-medium flex items-center gap-2"><Scissors size={16} className="text-rose-400" /> Shirt Styling</h3>
                    {expandedSections.shirtStyling ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  {expandedSections.shirtStyling && (
                    <div className="p-3 pt-0 border-t border-white/5">
                      <div className="flex justify-end pb-2 -mt-1">
                        <LoadStylePresetButton
                          section="shirt"
                          shirtStyling={shirtStyling}
                          setShirtStyling={setShirtStyling}
                          testId="qo-load-shirt-style"
                        />
                      </div>
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                        <StyleSelect label="Collar Style"   value={shirtStyling.collar_style}   onChange={(v) => setShirtStyling({...shirtStyling, collar_style: v})}   options={SHIRT_COLLAR_STYLES}   codePrefix="shirt_collar_style" />
                        <StyleSelect label="Collar Height"  value={shirtStyling.collar_height}  onChange={(v) => setShirtStyling({...shirtStyling, collar_height: v})}  options={SHIRT_COLLAR_HEIGHTS}  codePrefix="shirt_collar_height" />
                        <StyleSelect label="Collar Stays"   value={shirtStyling.collar_stays}   onChange={(v) => setShirtStyling({...shirtStyling, collar_stays: v})}   options={SHIRT_COLLAR_STAYS}   codePrefix="shirt_collar_stays" />
                        <StyleSelect label="Cuff Style"     value={shirtStyling.cuff_style}     onChange={(v) => setShirtStyling({...shirtStyling, cuff_style: v})}     options={SHIRT_CUFF_STYLES}     codePrefix="shirt_cuff_style" />
                        <StyleSelect label="Placket"        value={shirtStyling.placket}        onChange={(v) => setShirtStyling({...shirtStyling, placket: v})}        options={SHIRT_PLACKETS}        codePrefix="shirt_placket" />
                        <StyleSelect label="Pocket"         value={shirtStyling.pocket}         onChange={(v) => setShirtStyling({...shirtStyling, pocket: v})}         options={SHIRT_POCKETS}         codePrefix="shirt_pocket" />
                        <StyleSelect label="Back"           value={shirtStyling.back}           onChange={(v) => setShirtStyling({...shirtStyling, back: v})}           options={SHIRT_BACKS}           codePrefix="shirt_back" />
                        <StyleSelect label="Bottom"         value={shirtStyling.bottom}         onChange={(v) => setShirtStyling({...shirtStyling, bottom: v})}         options={SHIRT_BOTTOMS}         codePrefix="shirt_bottom" />
                        <StyleSelect label="Monogram"       value={shirtStyling.monogram}       onChange={(v) => setShirtStyling({...shirtStyling, monogram: v})}       options={SHIRT_MONOGRAMS}       codePrefix="shirt_monogram" />
                        <ButtonStyleSelect label="Button Style" category="shirt" value={shirtStyling.button_style} onChange={(v) => setShirtStyling({...shirtStyling, button_style: v})} testId="qo-shirt-button-style" />
                      </div>
                      <AutoStyleFields
                        section="shirt"
                        styling={shirtStyling}
                        setStyling={setShirtStyling}
                        excludeFields={[
                          "shirt_collar_style","shirt_collar_height","shirt_collar_stays",
                          "shirt_cuff_style","shirt_placket","shirt_pocket","shirt_back",
                          "shirt_bottom","shirt_button_style","shirt_monogram",
                        ]}
                        testIdPrefix="qo-shirt-auto-style"
                      />
                      <CustomStyleCodes
                        bucket="shirt"
                        value={shirtStyling.custom_codes || []}
                        onChange={(v) => setShirtStyling({ ...shirtStyling, custom_codes: v })}
                        testId="qo-shirt-custom-codes"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* General Notes */}
              <div className="bg-card border border-white/10 rounded-lg p-3">
                <h3 className="text-sm font-medium mb-2 flex items-center gap-2"><FileText size={16} className="text-slate-400" /> General Notes</h3>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional notes..." className="bg-background border-white/10 min-h-[80px] text-xs resize-none" data-testid="notes-input" />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Mockup Preview - Fixed Sidebar - Only show when Style Preview button is clicked */}
      {showMockupPreview && garmentType && (
        <div className="hidden xl:block fixed right-0 top-20 bottom-0 w-80 bg-card border-l border-white/10 z-40 overflow-hidden">
          <SuitMockupPreview
            garmentType={garmentType}
            jacketStyling={jacketStyling}
            pantStyling={pantStyling}
            vestStyling={vestStyling}
            shirtStyling={shirtStyling}
            fabricColor="Navy Blue"
            fabricPattern="Solid"
            isExpanded={showMockupPreview}
            onToggle={() => setShowMockupPreview(!showMockupPreview)}
          />
        </div>
      )}
      
      {/* Mobile Mockup Modal - Only show when Style Preview button is clicked */}
      <Dialog open={showMockupPreview && garmentType && typeof window !== 'undefined' && window.innerWidth < 1280} onOpenChange={setShowMockupPreview}>
        <DialogContent className="bg-card border-white/10 max-w-sm p-0">
          <SuitMockupPreview
            garmentType={garmentType}
            jacketStyling={jacketStyling}
            pantStyling={pantStyling}
            vestStyling={vestStyling}
            shirtStyling={shirtStyling}
            fabricColor="Navy Blue"
            fabricPattern="Solid"
            isExpanded={true}
            onToggle={() => setShowMockupPreview(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Save Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader><DialogTitle>Save Template</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-muted-foreground block mb-2">Template Name</label>
              <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="e.g., John Smith - Classic Fit" className="bg-background border-white/10" data-testid="template-name-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)} className="border-white/10">Cancel</Button>
            <Button onClick={handleSaveTemplate} disabled={isSaving} className="bg-emerald-500 hover:bg-emerald-600">
              {isSaving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Load Dialog */}
      <Dialog open={showLoadDialog} onOpenChange={setShowLoadDialog}>
        <DialogContent className="bg-card border-white/10 max-w-2xl">
          <DialogHeader><DialogTitle>Load Template</DialogTitle></DialogHeader>
          <div className="py-4 max-h-[400px] overflow-y-auto">
            {templates.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText size={32} className="mx-auto mb-2 opacity-50" /><p>No saved templates</p>
              </div>
            ) : (
              <div className="space-y-2">
                {templates.map((template) => (
                  <div key={template.id} className="flex items-center justify-between p-3 rounded-lg border border-white/10 hover:bg-white/5">
                    <div>
                      <p className="font-medium text-sm">{template.name}</p>
                      <p className="text-xs text-muted-foreground">{template.garment_type} • {new Date(template.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleLoadTemplate(template)} className="border-white/10 h-8"><Download size={12} className="mr-1" /> Load</Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteTemplate(template.id)} className="text-destructive hover:text-destructive h-8"><Trash2 size={12} /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminQuickOrderPage;
